import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotifierService } from '../services/Notifierservice';
import { catchError, tap, throwError } from 'rxjs';
import { VerifyService } from '../components/verify/service/verify.service';
import { AppSettings } from '../../app/app.settings';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export const authuserInterceptor: HttpInterceptorFn = (req, next): any => {
  const router = inject(Router);
  const notifier = inject(NotifierService);
  const verifyService = inject(VerifyService);

  const hexTimestamp = verifyService.dateTimeToHex();
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';

  // Routes that never require auth — used to decide whether a 401/403
  // should trigger a forced logout/redirect.

  // session_token cookie is HttpOnly — browser attaches it automatically,
  // we never read or set it manually. withCredentials ensures it's sent.
  //
  // csrf_access_token cookie is NOT HttpOnly, by design — Flask-JWT-Extended
  // expects the frontend to read it and echo it back as a header so the
  // backend can verify same-origin JS handled the request (CSRF protection).
  const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
    req.method.toUpperCase(),
  );
  const csrfCookieName = AppSettings.csrfCookieName; // e.g. 'uat_csrf_access_token'
  const csrfToken = needsCsrf ? getCookie(csrfCookieName) : null;
  const tokenRequest = req.clone({
    setHeaders: {
      'x-meta-token': hexTimestamp,
      ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
    },
    withCredentials: true,
  });

  return next(tokenRequest).pipe(
    tap((event: any) => {
      if (event instanceof HttpResponse) {
        // clear previous notifications on success, if needed
      }
    }),
    catchError((error: HttpErrorResponse) => {
      let message = 'Something went wrong. Please try again later.';

      const handleAuthError = () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document
          .querySelectorAll('.modal-backdrop')
          .forEach((el) => el.remove());

        localStorage.clear();
        sessionStorage.clear();
        router.navigateByUrl('login').then(() => {});
      };

      switch (error.status) {
        case 0:
          message = navigator.onLine
            ? 'We are unable to reach the server at this time. Please try again shortly.'
            : 'No internet connection detected. Kindly check your network connection and try again.';
          break;

        case 400:
          message =
            error?.error?.message ||
            'Invalid request. Please check your input.';
          break;

        case 401:
          message =
            error?.error?.message || 'Session expired. Please login again.';
          if (isLoggedIn) {
            handleAuthError();
          }
          break;

        case 403:
          message =
            error?.error?.message ||
            'Access denied or session expired. Please login again.';
          if (isLoggedIn) {
            handleAuthError();
          }
          break;

        case 404:
          message = error?.error?.message || 'Requested resource not found.';
          break;

        case 500:
          message =
            error?.error?.message || 'Server error. Please try again later.';
          break;

        default:
          message = error?.error?.message || message;
          break;
      }

      notifier.notify('error', message);
      console.error('HTTP Error:', error);

      return throwError(() => error);
    }),
  );
};
